/**
 * Workspace Plan Page
 *
 * §2.4 proof-of-life surface for the commercial plan layer (migration 498,
 * src/lib/entitlements.ts). Renders the persisted WorkspacePlanRecord that
 * lives in `workspace_plans` and is loaded into the tenant store on every
 * workspace activation.
 *
 * Read-only for workspace members. Plan changes require super-admin access
 * (billing/ops path via service role).
 *
 * Route: /prioritization/settings/plan
 */

import { BadgeCheck, Clock, ShieldCheck, Zap, Package } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import {
  resolveEntitlements,
  PLAN_LABELS,
  ADDON_LABELS,
  type PlanStatus,
  type EntitlementKey,
} from '../../../lib/entitlements';

// ── Status display helpers ────────────────────────────────────────────────────

const STATUS_STYLES: Record<PlanStatus, { dot: string; text: string; label: string }> = {
  trialing:  { dot: 'bg-[var(--slate)]',    text: 'text-[var(--text-ter)]',    label: 'Trialing' },
  active:    { dot: 'bg-[var(--green)]', text: 'text-[var(--green)] dark:text-[var(--text-ter)]', label: 'Active' },
  past_due:  { dot: 'bg-[var(--mustard)]',   text: 'text-[var(--mustard)]',   label: 'Past Due' },
  suspended: { dot: 'bg-[var(--coral)]',    text: 'text-[var(--coral)]',    label: 'Suspended' },
  cancelled: { dot: 'bg-[var(--surface)]',    text: 'text-[var(--text-sec)]',    label: 'Cancelled' },
};

const TIER_STYLES: Record<string, string> = {
  pilot:        'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
  organization: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
  enterprise:   'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
};

// Group entitlement keys for display
const ENTITLEMENT_GROUPS: { label: string; keys: EntitlementKey[] }[] = [
  {
    label: 'Core Platform',
    keys: [
      'prioritization_cycles',
      'planning_domain',
      'catchball',
      'workspace_admin_console',
      'rest_api',
      'mcp_endpoint',
      'connectors',
      'webhooks',
      'audit_governance_tables',
    ],
  },
  {
    label: 'Identity & Security',
    keys: ['sso_saml', 'scim_provisioning', 'mfa_enforcement', 'ip_allowlisting'],
  },
  {
    label: 'Compliance & Data',
    keys: ['audit_log_api', 'data_residency', 'soc2_report', 'hmac_webhook_signing', 'dedicated_infrastructure'],
  },
  {
    label: 'Service',
    keys: ['guided_implementation', 'priority_support', 'custom_connector_development'],
  },
];

const ENTITLEMENT_LABELS: Record<EntitlementKey, string> = {
  prioritization_cycles:      'Prioritization Cycles',
  planning_domain:            'Planning Domain',
  catchball:                  'Catchball',
  workspace_admin_console:    'Workspace Admin Console',
  rest_api:                   'REST API',
  mcp_endpoint:               'MCP Endpoint',
  connectors:                 'Connectors (write)',
  webhooks:                   'Webhooks (write)',
  audit_governance_tables:    'Audit & Governance Tables',
  sso_saml:                   'SSO / SAML',
  scim_provisioning:          'SCIM Provisioning',
  mfa_enforcement:            'MFA Enforcement',
  audit_log_api:              'Audit Log API',
  data_residency:             'Data Residency',
  soc2_report:                'SOC 2 Type II Report',
  hmac_webhook_signing:       'HMAC-SHA256 Webhook Signing',
  ip_allowlisting:            'IP Allowlisting',
  dedicated_infrastructure:   'Dedicated Infrastructure',
  guided_implementation:      'Guided Implementation',
  priority_support:           'Priority Support & SLA',
  custom_connector_development: 'Custom Connector Development',
};

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function WorkspacePlanPage() {
  const workspacePlan = useTenantStore((s) => s.workspacePlan);

  if (!workspacePlan) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-sm text-[var(--text-ter)]">
        Loading plan…
      </div>
    );
  }

  const entitlementSet = resolveEntitlements(workspacePlan);
  const status = STATUS_STYLES[workspacePlan.status] ?? STATUS_STYLES.active;
  const tierStyle = TIER_STYLES[workspacePlan.tier] ?? TIER_STYLES.organization;

  return (
    <div>
      <PageHeader
        title="Workspace Plan"
        subtitle="Your workspace's commercial plan, entitlements, and usage limits — as recorded in workspace_plans."
        icon={<BadgeCheck className="h-4 w-4 text-[var(--green)]" />}
      />

      <div className="mt-6 space-y-6">
        {/* ── Plan summary card ─────────────────────────────────────────── */}
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
          <div className="flex flex-wrap items-start gap-4">
            {/* Tier badge */}
            <div className="flex-1 min-w-[160px]">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] mb-1">Tier</p>
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-semibold ${tierStyle}`}>
                <Package className="h-3.5 w-3.5" />
                {PLAN_LABELS[workspacePlan.tier]}
              </span>
            </div>

            {/* Status */}
            <div className="flex-1 min-w-[140px]">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] mb-1">Status</p>
              <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${status.text}`}>
                <span className={`h-2 w-2 rounded-full ${status.dot}`} />
                {status.label}
              </span>
            </div>

            {/* Term */}
            <div className="flex-1 min-w-[200px]">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] mb-1">Contract Term</p>
              <p className="text-sm text-[var(--text-sec)] flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                {workspacePlan.termStartsAt || workspacePlan.termEndsAt
                  ? `${formatDate(workspacePlan.termStartsAt)} – ${formatDate(workspacePlan.termEndsAt)}`
                  : 'No fixed term'}
              </p>
            </div>

            {/* Seats */}
            {workspacePlan.seats != null && (
              <div className="flex-1 min-w-[120px]">
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] mb-1">Contracted Seats</p>
                <p className="text-sm text-[var(--text-sec)]">{workspacePlan.seats.toLocaleString()}</p>
              </div>
            )}
          </div>

          {/* Add-ons */}
          {workspacePlan.addOns.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[var(--rule)]">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] mb-2">Purchased Add-ons</p>
              <div className="flex flex-wrap gap-2">
                {workspacePlan.addOns.map((key) => (
                  <span
                    key={key}
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--slate-dim)] border border-[var(--slate)] px-2.5 py-0.5 text-xs font-medium text-[var(--text-sec)]"
                  >
                    <Zap className="h-3 w-3" />
                    {ADDON_LABELS[key] ?? key}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Usage limits ─────────────────────────────────────────────── */}
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-4 w-4 text-[var(--text-ter)]" />
            <h3 className="text-sm font-semibold text-[var(--text-pri)]">Usage Limits</h3>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {(
              [
                ['Max Users',              entitlementSet.limits.maxUsers],
                ['Max Active Cycles',      entitlementSet.limits.maxActiveCycles],
                ['Max Sessions / Month',   entitlementSet.limits.maxSessionsPerMonth],
                ['Max Connectors',         entitlementSet.limits.maxConnectors],
              ] as [string, number | null][]
            ).map(([label, val]) => (
              <div key={label} className="border border-[var(--rule)] bg-[var(--surface)] px-3 py-2.5">
                <p className="text-xs text-[var(--text-ter)] mb-0.5">{label}</p>
                <p className="text-sm font-semibold text-[var(--text-pri)]">
                  {val === null ? 'Unlimited' : val.toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* ── Entitlements ─────────────────────────────────────────────── */}
        <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
          <div className="flex items-center gap-2 mb-4">
            <BadgeCheck className="h-4 w-4 text-[var(--text-ter)]" />
            <h3 className="text-sm font-semibold text-[var(--text-pri)]">Entitlements</h3>
            <span className="ml-auto text-xs text-[var(--text-ter)]">
              Plan is {entitlementSet.active ? 'active' : 'inactive'}
            </span>
          </div>
          <div className="space-y-4">
            {ENTITLEMENT_GROUPS.map((group) => (
              <div key={group.label}>
                <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-ter)] mb-1.5">{group.label}</p>
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {group.keys.map((key) => {
                    const granted = entitlementSet.entitlements[key];
                    return (
                      <div
                        key={key}
                        className={`flex items-center gap-2  px-2.5 py-1.5 text-xs ${
                          granted
                            ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                            : 'bg-[var(--surface)] text-[var(--text-ter)]'
                        }`}
                      >
                        <span className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${granted ? 'bg-[var(--green)]' : 'bg-[var(--rule-bright)]'}`} />
                        {ENTITLEMENT_LABELS[key]}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Data source note ─────────────────────────────────────────── */}
        <p className="text-xs text-[var(--text-ter)]">
          Source: <code className="rounded bg-[var(--hover-tint)] px-1 py-0.5 text-[var(--text-ter)]">workspace_plans</code> table ·
          loaded from Supabase on workspace activation ·
          plan changes require super-admin access (billing/ops path)
        </p>
      </div>
    </div>
  );
}
